import { useEffect, useState } from 'react';
import { getAuthService } from '@tagna/auth';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@tagna/auth';

const REDIRECT_PATH_KEY = 'redirectPath';
const LOGIN_REDIRECT_LOCK_KEY = 'admin_login_redirect_lock';
const LOGIN_REDIRECT_LOCK_TTL_MS = 15_000;

export function SignInPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const [status, setStatus] = useState<'idle' | 'redirecting' | 'error'>(
    'idle',
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isAuthenticated) {
      window.location.replace('/');
      return;
    }

    let isMounted = true;
    const startLogin = async () => {
      sessionStorage.setItem(REDIRECT_PATH_KEY, '/');

      const now = Date.now();
      const lockValue = Number(sessionStorage.getItem(LOGIN_REDIRECT_LOCK_KEY) || '0');
      if (Number.isFinite(lockValue) && now - lockValue < LOGIN_REDIRECT_LOCK_TTL_MS) {
        return;
      }
      sessionStorage.setItem(LOGIN_REDIRECT_LOCK_KEY, String(now));

      const authService = getAuthService();

      if (!authService) {
        if (!isMounted) return;
        sessionStorage.removeItem(LOGIN_REDIRECT_LOCK_KEY);
        setStatus('error');
        setErrorMessage(
          'Authentication service is not ready. Please refresh and try again.',
        );
        return;
      }

      try {
        if (!isMounted) return;
        setStatus('redirecting');
        await authService.login();
      } catch (error) {
        console.error('Failed to start login redirect:', error);
        if (!isMounted) return;
        sessionStorage.removeItem(LOGIN_REDIRECT_LOCK_KEY);
        setStatus('error');
        setErrorMessage(
          'Unable to contact the identity service. Please check your connection and try again.',
        );
      }
    };

    void startLogin();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isLoading]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="max-w-sm w-full text-center space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Signing you in…
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Redirecting to Tagna Identity to continue.
          </p>
        </div>

        {status !== 'error' && (
          <div className="flex items-center justify-center">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        )}

        {status === 'error' && errorMessage && (
          <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-md text-sm">
            {errorMessage}
          </div>
        )}

        <p className="text-xs text-gray-400">
          If nothing happens, ensure https://localhost:44311 is trusted in your
          browser, then refresh this page.
        </p>
      </div>
    </div>
  );
}
